import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress, Avatar, IconButton } from '@mui/material';
import { Tree, TreeNode } from 'react-organizational-chart';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import api from '../../api';
import EmployeeCardPopup from './EmployeeCardPopup';

// Icons
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;

const OrgChartNode = ({ node, level = 0, currentUserEmpId, onNodeClick }) => {
  // Changed from level < 2 to false to collapse all child nodes by default
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isCurrentUser = node.id === currentUserEmpId;

  return (
    <TreeNode
      label={
        <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', pb: hasChildren ? 2 : 0 }}>
          <Paper 
            elevation={isCurrentUser ? 6 : 2} 
            sx={{ 
              padding: '12px 16px', 
              display: 'inline-block', 
              borderRadius: '10px',
              borderTop: isCurrentUser ? '6px solid #ff9800' : '4px solid #1a237e',
              border: isCurrentUser ? '1px solid #ffb74d' : 'none',
              minWidth: '170px',
              bgcolor: isCurrentUser ? '#fff8e1' : '#ffffff',
              boxShadow: isCurrentUser ? '0 0 15px rgba(255, 152, 0, 0.3)' : '0 4px 6px rgba(0,0,0,0.05)',
              position: 'relative',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Avatar 
                src={`${baseUrl}/employees/${node.id}/profile-picture`}
                sx={{ 
                  width: 45, height: 45, 
                  bgcolor: isCurrentUser ? '#ffcc80' : '#e8eaf6', 
                  color: isCurrentUser ? '#e65100' : '#1a237e', 
                  fontSize: '1.2rem', fontWeight: 'bold', mb: 0.5, 
                  border: isCurrentUser ? '2px solid #ff9800' : '2px solid #e8eaf6' 
                }}
              >
                {node.name.charAt(0).toUpperCase()}
              </Avatar>

              <Typography 
                variant="subtitle2" 
                onClick={(e) => {
                  e.stopPropagation();
                  onNodeClick(node.id);
                }}
                sx={{ 
                  fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, 
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                }}
              >
                {node.name} {isCurrentUser && <span style={{ color: '#e65100' }}>(You)</span>}
              </Typography>
              <Typography variant="caption" sx={{ color: isCurrentUser ? '#e65100' : '#546e7a', fontWeight: 600 }}>
                {node.designation}
              </Typography>
            </Box>

            {hasChildren && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                sx={{
                  position: 'absolute', bottom: '-14px', left: '50%', transform: 'translateX(-50%)',
                  bgcolor: '#ffffff', border: '1px solid #cfd8dc', boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: '#f5f5f5' }, width: 26, height: 26, zIndex: 2
                }}
              >
                {isExpanded ? <RemoveIcon sx={{ fontSize: 18, color: '#d32f2f' }} /> : <AddIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
              </IconButton>
            )}
          </Paper>
        </Box>
      }
    >
      {isExpanded && hasChildren && node.children.map(child => (
        <OrgChartNode key={child.id} node={child} level={level + 1} currentUserEmpId={currentUserEmpId} onNodeClick={onNodeClick} />
      ))}
    </TreeNode>
  );
};

const OrgChartSection = () => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Popup States
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState(null);

  const rawEmpId = localStorage.getItem('emp_id') || '';
  const currentUserEmpId = rawEmpId.trim().toUpperCase();

  useEffect(() => {
    api.get('/dashboard/org-chart')
      .then(res => {
        setTreeData(res.data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching org chart", err);
        setLoading(false);
      });
  }, []);

  const handleNodeClick = (empId) => {
    setSelectedEmpId(empId);
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedEmpId(null);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }

  if (treeData.length === 0) {
    return <Typography sx={{ mt: 5, textAlign: 'center', color: 'text.secondary' }}>No active organization structure found.</Typography>;
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ width: '100%', height: '70vh', position: 'relative', border: '1px solid #e0e0e0', borderRadius: 3, bgcolor: '#f5f7fa', overflow: 'hidden' }}
    >
      <TransformWrapper initialScale={1} minScale={0.2} maxScale={2} centerOnInit={true} wheel={{ step: 0.1 }}>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <React.Fragment>
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'rgba(255,255,255,0.9)', p: 0.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backdropFilter: 'blur(4px)' }}>
              <IconButton onClick={() => zoomIn()} size="small" sx={{ color: '#1a237e' }}><ZoomInIcon /></IconButton>
              <IconButton onClick={() => zoomOut()} size="small" sx={{ color: '#1a237e' }}><ZoomOutIcon /></IconButton>
              <IconButton onClick={() => resetTransform()} size="small" sx={{ color: '#d32f2f' }}><RestartAltIcon /></IconButton>
            </Box>

            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <Box sx={{ p: 6, minWidth: 'min-content' }}>
                {treeData.map((rootNode, index) => (
                  <Box key={index} sx={{ mb: 6 }}>
                    <Tree
                      lineWidth={'2px'} lineColor={'#90a4ae'} lineBorderRadius={'10px'}
                      label={<OrgChartNode node={rootNode} level={0} currentUserEmpId={currentUserEmpId} onNodeClick={handleNodeClick} />}
                    >
                    </Tree>
                  </Box>
                ))}
              </Box>
            </TransformComponent>
          </React.Fragment>
        )}
      </TransformWrapper>

      <EmployeeCardPopup 
        open={popupOpen} 
        onClose={handleClosePopup} 
        empId={selectedEmpId} 
      />
    </Paper>
  );
};

export default OrgChartSection;